import { useState } from "react";
import { Play } from "lucide-react";
import { useConvex } from "convex/react";
import { api } from "../../../lib/convexApi";

// The tester now calls api.policy.queries.classifyCommand — the SAME layer set
// the engine enforces.
//
// It previously called firewall.queries.testCommand, which knows only 7 builtin
// denies, so it reported "ALLOW · no rule matched (default-allow)" for commands
// production blocks (e.g. `mkfs /dev/sda`, denied by destructive/format-disk)
// and was blind to active exceptions. A security tester that disagrees with the
// engine manufactures false confidence, so the two now share one classifier.
//
// We use useConvex().query() (one-shot) rather than useQuery (reactive)
// because the user is explicitly triggering a single test on Play —
// reactivity would re-run on unrelated rule changes, which we don't want here.

const LAYER_LABELS: Record<string, string> = {
  exception: "granted exception",
  "firewall-builtin": "built-in firewall",
  "firewall-custom": "custom firewall rule",
  "terminal-signal": "terminal signal classifier",
  egress: "egress allow-list",
  "pack-builtin": "built-in policy pack",
  "pack-uploaded": "uploaded policy pack",
};

interface Match {
  layer: string;
  decision: string;
  ruleId: string;
  packId: string | null;
  reason: string | null;
  // False when the layer matched but governs nothing in production.
  live?: boolean;
}

interface Surface {
  decision: string;
  matched: Match | null;
}

interface Result {
  decision: "allow" | "ask" | "deny";
  enforced: boolean;
  mode: string;
  matched: Match | null;
  matches: Match[];
  surfaces: { terminal: Surface; hooks: Surface; policy: Surface };
  truncated: boolean;
  modeScope?: string;
  error?: string;
}

const EMPTY_SURFACE = { decision: "deny", matched: null };
const EMPTY_SURFACES = {
  terminal: EMPTY_SURFACE,
  hooks: EMPTY_SURFACE,
  policy: EMPTY_SURFACE,
};

const SURFACE_LABELS: Record<string, string> = {
  terminal: "terminal (PTY)",
  hooks: "agent hooks",
  policy: "exceptions",
};

// Any disagreement between the surfaces is worth showing: the operator's real
// question is "will this run?", and three different answers means the answer
// depends on how the command arrives.
function surfacesDisagree(result: Result): boolean {
  const { terminal, hooks, policy } = result.surfaces;
  return new Set([terminal.decision, hooks.decision, policy.decision]).size > 1;
}

export function PolicyTester() {
  const convex = useConvex();
  const [command, setCommand] = useState("");
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);

  async function run() {
    if (!command.trim()) return;
    setBusy(true);
    try {
      const res = (await convex.query(api.policy.queries.classifyCommand, {
        command,
      })) as Partial<Omit<Result, "error">>;
      const decision =
        res.decision === "deny" || res.decision === "ask" ? res.decision : ("allow" as const);
      // Normalize the shape rather than spreading it raw. The render path
      // dereferences surfaces.*.decision and matches.length; a backend older
      // (or newer) than this bundle would crash the pane on a field it never
      // sent, and this pane is where an operator goes when they are unsure
      // whether something is blocked.
      setResult({
        ...res,
        decision,
        matched: res.matched ?? null,
        matches: Array.isArray(res.matches) ? res.matches : [],
        surfaces: {
          terminal: res.surfaces?.terminal ?? EMPTY_SURFACE,
          hooks: res.surfaces?.hooks ?? EMPTY_SURFACE,
          policy: res.surfaces?.policy ?? EMPTY_SURFACE,
        },
        truncated: Boolean(res.truncated),
        enforced: Boolean(res.enforced),
        mode: res.mode ?? "unknown",
      });
    } catch (e) {
      // Fail closed in the UI too: an unknown verdict is shown as deny, not as
      // a reassuring allow.
      setResult({
        decision: "deny",
        enforced: false,
        mode: "unknown",
        matched: null,
        matches: [],
        surfaces: EMPTY_SURFACES,
        truncated: false,
        error: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sr-tester">
      <div className="sr-tester__row">
        <span className="sr-tester__label">Test command:</span>
        <input
          type="text"
          className="sr-tester__input"
          value={command}
          onChange={(e) => setCommand(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void run();
          }}
          placeholder="cat .env.production"
        />
        <button
          type="button"
          className="sr-btn"
          onClick={run}
          disabled={busy || !command.trim()}
          aria-label="Run test"
        >
          <Play size={14} />
        </button>
      </div>
      {result && (
        <div className="sr-tester__result">
          ↳ <strong className={`is-${result.decision}`}>{result.decision.toUpperCase()}</strong>
          {result.matched && (
            <>
              {" "}
              via {LAYER_LABELS[result.matched.layer] ?? result.matched.layer} (
              <code>{result.matched.ruleId}</code>)
            </>
          )}
          {/* "Denied" and "would be denied if enforcement were on" are different
              answers, and conflating them is how an operator ends up believing
              a control is live when it is in observe mode. */}
          {/* Derived from the TERMINAL surface, not the headline. Deriving it
              from `decision` made this claim "a terminal started with
              enforcement on still holds this for approval" for a verdict the
              PACK layer produced — which the terminal never sees. In observe
              mode `npm install lodash` printed that sentence two lines above
              "dependency_install matched but has no enforcement path — it will
              not stop this command." Self-contradicting, on a security pane. */}
          {!result.error && result.surfaces.terminal.decision !== "allow" && !result.enforced && (
            <span className="sr-tester__mode">
              {" "}
              — org enforcement mode is {result.mode}, which gates the egress stage only. A terminal
              started with enforcement on still{" "}
              {result.surfaces.terminal.decision === "ask"
                ? "holds this for approval"
                : "declines this"}
              .
            </span>
          )}
          {result.matched?.reason && (
            <div className="sr-tester__reason">"{result.matched.reason}"</div>
          )}
          {!result.matched && !result.error && (
            <div className="sr-tester__reason">no rule matched (default-allow)</div>
          )}
          {/* An ERROR is not a verdict, so it must not borrow the observe-mode
              wording — "DENY — not enforced (mode: unknown)" reads like a
              classification when nothing was classified. */}
          {result.error && (
            <>
              <span className="sr-tester__mode"> — could not classify</span>
              <div className="sr-tester__reason">{result.error}</div>
            </>
          )}
          {/* When the two surfaces disagree, saying so is the whole point: an
              exception that allows the command does NOT reach the terminal
              stream, and an operator who reads only the headline would grant
              an exception and believe the command now runs. */}
          {!result.error && surfacesDisagree(result) && (
            <div className="sr-tester__surfaces">
              {(["terminal", "hooks", "policy"] as const).map((key) => {
                const surface = result.surfaces[key];
                return (
                  <span className="sr-tester__surface" key={key}>
                    {SURFACE_LABELS[key]}{" "}
                    <strong className={`is-${surface.decision}`}>
                      {surface.decision.toUpperCase()}
                    </strong>
                    {surface.matched && (
                      <>
                        {" "}
                        (<code>{surface.matched.ruleId}</code>)
                      </>
                    )}
                  </span>
                );
              })}
              <div className="sr-tester__reason">
                These paths evaluate different rule sets — the verdict above is the strictest,
                because that is what the command will hit.
                {result.surfaces.policy.matched?.layer === "exception" &&
                  " Exceptions do not reach the terminal or the agent hooks."}
              </div>
            </div>
          )}
          {/* Split by what the rule was TRYING to do. "will not stop this
              command" is the right warning for a deny rule and the exact
              opposite of the truth for an allow rule, whose whole purpose is to
              let the command through — one sentence for both told an admin
              their allow-rule was failing to block. */}
          {!result.error &&
            (() => {
              const dead = result.matches.filter((m) => m.live === false);
              if (dead.length === 0) return null;
              const blocking = dead.filter((m) => m.decision !== "allow");
              const permitting = dead.filter((m) => m.decision === "allow");
              const ids = (rows: Match[]) => rows.map((m) => m.ruleId).join(", ");
              return (
                <div className="sr-tester__reason sr-tester__warn">
                  {blocking.length > 0 && (
                    <div>
                      {ids(blocking)} matched but {blocking.length === 1 ? "has" : "have"} no
                      enforcement path — {blocking.length === 1 ? "it" : "they"} will not stop this
                      command.
                    </div>
                  )}
                  {permitting.length > 0 && (
                    <div>
                      {ids(permitting)} matched but {permitting.length === 1 ? "is" : "are"} not
                      consulted by anything that runs — this command is not permitted because of{" "}
                      {permitting.length === 1 ? "it" : "them"}.
                    </div>
                  )}
                </div>
              );
            })()}
          {result.truncated && (
            <div className="sr-tester__reason">
              Command exceeds 64 KiB — classified on the same prefix the engine evaluates, so rules
              matching only past that point are not shown.
            </div>
          )}
          {result.matches.length > 1 && (
            <div className="sr-tester__trace">
              {result.matches.length} rules matched across{" "}
              {new Set(result.matches.map((m) => m.layer)).size} layers:{" "}
              {result.matches
                .slice(0, 6)
                .map((m) => m.ruleId)
                .join(", ")}
              {result.matches.length > 6 && ` +${result.matches.length - 6} more`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
